import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import CustomSwitch from '../customSwitch';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish5}`,
    borderRadius: 8,
    cursor: 'default',
    display: 'grid',
    gridTemplateColumns: 'fit-content(0) 1fr fit-content(0)',
    alignItems: 'center',
    gap: 8,
    padding: spacing(2, 4, 2, 2),
    transition: 'border-color .3s ease',
    height: 52,
    '&$selected:hover': {
      borderColor: primary.blue1,
    },
  },
  selected: {
    borderColor: primary.blue1,
  },
  selectable: {
    cursor: 'pointer',
    '&:hover': {
      borderColor: primary.bluish1,
    },
  },
  label: {
    color: primary.bluish5,
    transition: 'color .3s ease',
    userSelect: 'none',
  },
  labelActive: {
    color: primary.bluish1,
  },
  switch: {
    width: 32,
  },
  infoWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  info: {
    color: primary.bluish5,
    transition: 'color .3s ease',
  },
  infoActive: {
    color: primary.bluish1,
  },
  icon: {
    width: 24,
    height: 24,
    '& path': {
      fill: primary.bluish5,
      transition: 'fill .3s ease',
    },
  },
  iconActive: {
    '& path': {
      fill: primary.bluish1,
    },
  },
}));

const ToggleableSettingsField = ({
  translations,
  label,
  icon: Icon,
  info = '',
  value = {},
  isActive,
  settingsFor,
  isSubSetting = false,
  onSelect,
  onToggle,
}) => {
  const classes = useStyles();

  const isSelected = isSubSetting
    ? value?.sub === settingsFor
    : value?.main === settingsFor;

  const handleSelect = () => {
    if (isActive && !isSelected) {
      onSelect({
        ...value,
        ...(isSubSetting ? { sub: settingsFor } : { main: settingsFor }),
      });
    }
  };

  const handleToggle = isChecked => onToggle(isChecked);

  return (
    <div
      className={classNames(classes.root, {
        [classes.selected]: isSelected,
        [classes.selectable]: isActive,
      })}
      onClick={handleSelect}
    >
      <Icon
        className={classNames(classes.icon, { [classes.iconActive]: isActive })}
      />
      <div>
        <Typography
          className={classNames(classes.label, {
            [classes.labelActive]: isActive,
          })}
          variant="subtitle1"
        >
          {label}
        </Typography>
        <Typography
          className={classNames(classes.info, {
            [classes.infoActive]: isActive,
          })}
          variant="body2"
        >
          {`${translations.info} ${isActive ? info : '-'}`}
        </Typography>
      </div>
      <CustomSwitch
        className={classes.switch}
        isChecked={isActive}
        tooltipText={
          isActive ? translations.tooltip.inactive : translations.tooltip.active
        }
        onChange={handleToggle}
      />
    </div>
  );
};

ToggleableSettingsField.propTypes = {
  translations: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.object,
  info: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  isSubSetting: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default ToggleableSettingsField;
